import React, { useEffect, useMemo, useState } from 'react';
import { CheckSquare, RefreshCw } from 'lucide-react';
import api from '../services/api';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import './Users.css';
import './TasksRewards.css';

const STATUSES = ['todo', 'in_progress', 'completed', 'cancelled'];

const MyTasks = () => {
  useMarkAsReadOnMount(['task']);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [taskFilter, setTaskFilter] = useState('all');

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.tasks)) return value.tasks;
    return [];
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tasks/me');
      setTasks(normalizeArray(res));
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load my tasks.' });
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const updateTask = async (id, payload) => {
    try {
      await api.patch(`/tasks/${id}`, payload);
      await loadTasks();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update task.' });
    }
  };

  const currentTasks = useMemo(
    () => tasks.filter((t) => String(t.status).toLowerCase() !== 'completed'),
    [tasks],
  );

  const completedTasks = useMemo(
    () => tasks.filter((t) => String(t.status).toLowerCase() === 'completed').length,
    [tasks],
  );

  const filteredTasks = useMemo(() => {
    if (taskFilter === 'open') {
      return tasks.filter((t) => String(t.status).toLowerCase() !== 'completed');
    }
    if (taskFilter === 'completed') {
      return tasks.filter((t) => String(t.status).toLowerCase() === 'completed');
    }
    return tasks;
  }, [tasks, taskFilter]);

  if (loading) return <div className="page-loading">Loading my tasks...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><CheckSquare size={24} /> My Tasks</h1>
        <button className="btn btn-secondary" onClick={loadTasks}><RefreshCw size={14} /> Refresh</button>
      </div>

      {message.text && <div className={`remote-message ${message.type}`}>{message.text}</div>}

      <div className="tr-stats-grid">
        <div className="tr-stat-card">
          <div className="tr-stat-label">Current Tasks</div>
          <div className="tr-stat-value">{currentTasks.length}</div>
        </div>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Completed</div>
          <div className="tr-stat-value">{completedTasks}</div>
        </div>
      </div>

      <div className="users-table-container tr-card">
        <div className="list-meta list-meta-pad">
          Showing <strong>{filteredTasks.length}</strong> task{filteredTasks.length === 1 ? '' : 's'}
        </div>
        <div style={{ padding: '0 16px 10px 16px' }}>
          <div className="tr-filter-tabs">
          <button
            className={`tr-filter-btn ${taskFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTaskFilter('all')}
            type="button"
          >
            All
          </button>
          <button
            className={`tr-filter-btn ${taskFilter === 'open' ? 'active' : ''}`}
            onClick={() => setTaskFilter('open')}
            type="button"
          >
            Open
          </button>
          <button
            className={`tr-filter-btn ${taskFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setTaskFilter('completed')}
            type="button"
          >
            Completed
          </button>
          </div>
        </div>
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Estimated</th>
              <th>Actual</th>
              <th>Due</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length ? filteredTasks.map((t) => (
              <tr key={t.id}>
                <td>#{t.id}</td>
                <td>
                  <div>{t.title}</div>
                  {t.description ? <small style={{ opacity: 0.8 }}>{t.description}</small> : null}
                </td>
                <td>
                  <span className={`tr-badge tr-priority-${String(t.priority).toLowerCase()}`}>
                    {t.priority}
                  </span>
                </td>
                <td>
                  <select className="tr-select tr-tight-select" value={t.status} onChange={(e) => updateTask(t.id, { status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div style={{ marginTop: 6 }}>
                    <span className={`tr-badge tr-status-${String(t.status).toLowerCase()}`}>{t.status}</span>
                  </div>
                </td>
                <td>{t.estimatedHours ?? '-'}</td>
                <td>
                  <input
                    type="number"
                    step="0.5"
                    defaultValue={t.actualHours ?? 0}
                    onBlur={(e) => updateTask(t.id, { actualHours: Number(e.target.value || 0) })}
                    className="tr-input"
                    style={{ width: 80, padding: '6px 8px' }}
                  />
                </td>
                <td>{t.dueDate || '-'}</td>
              </tr>
            )) : (
              <tr><td colSpan="7" className="empty-row">No tasks found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyTasks;
